/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 */


'use strict';

import {
    ClientProperties,
    EntityBase
} from "./entitybase";
import {
    deepClone,
    Dictionary,
    tempID
} from '../utils/tools';
import {
    EntityStates,
    EntityTypes
} from '../utils/dvtypes';
import * as tools from "../utils/tools";

class StyleDefinition {
    /**
     * Create a new definition object for a Style object.
     * @param {object} [defaults = null] - An object that contains the default entity values for this class.
     * @class
     * @classdesc Style Definition object
     */
    constructor (defaults = null) {
        this.Description = defaults && defaults.Description != null ? defaults.Description : "";
        this.ItemKeys = defaults && Array.isArray(defaults.ItemKeys) ? defaults.ItemKeys : [];
        this.ItemValues = defaults && Array.isArray(defaults.ItemValues) ? defaults.ItemValues : [];
        this.Name = defaults && defaults.Name != null ? defaults.Name : "";
        this.UserCreated = defaults && defaults.UserCreated != null ? defaults.UserCreated : "";
        this.UserLastModified = defaults && defaults.UserLastModified != null ? defaults.UserLastModified : "";
        this.CurrentVersion = defaults && defaults.CurrentVersion != null ? defaults.CurrentVersion : "";

        /**
         * Store the version of the application on the definition when it is created.
         * @type {string}
         */
        this.ClientVersion = app.version;

    }

    /**
     * Dispose of entity references and structures safely.
     */
    dispose () {
        this.ItemKeys.length = 0;
        this.ItemValues.length = 0;
    }
}

export class Style extends EntityBase {
    /**
     * Create a new Style object
     * @param {object} [defaults = null] - An object that contains the default entity values for this class.
     * @class
     * @classdesc Style Object
     */
    constructor (defaults = null) {
        super(defaults);
        this.StyleID = defaults && defaults.StyleID != null ? defaults.StyleID : tempID();
        if (defaults && defaults.UID != null) {
            this.UID = defaults.UID;
        }
        if (defaults && typeof defaults.Definition === 'string') {
            try {
                defaults.Definition = JSON.parse(defaults.Definition);
            }
            catch (e) {
                defaults.Definition = new StyleDefinition();
            }
        }
        this.Definition = new StyleDefinition(defaults && defaults.Definition ? defaults.Definition : null);

        /**
         * Create a dictionary object based on the definition arrays.
         * @type {Dictionary}
         */
        let items = new Dictionary();
        items.Keys = this.Definition.ItemKeys;
        items.Values = this.Definition.ItemValues;
        items.KeysUID = [].repeat(this.Definition.ItemKeys.length, null);

        /**
         * Add base client properties to this object.
         * @type {ClientProperties}
         */
        this.clientProperties = new ClientProperties(EntityTypes.Style);

        /**
         * By default, this style is not "pinned" (pinned means not passed on to children controls when style is applied).
         * @type {boolean}
         */
        this.clientProperties.isPinned = false;

        /**
         * Add the items for the style targets of this style.
         * @type {Dictionary}
         */
        this.clientProperties.items = items;

        /**
         * Add a dictionary for any expressions used by the style targets.
         * @type {Dictionary}
         */
        this.clientProperties.expressions = new Dictionary();

        /**
         * Flag to indicate if this entity has been disposed.
         * @type {boolean}
         */
        this.isDisposed = false;
    }

    /**
     * Dispose of entity references and structures safely.
     */
    dispose () {
        if (this.isDisposed) {
            return;
        }
        this.clientProperties.items.clear();
        this.clientProperties.items = null;
        this.clientProperties.expressions.forEach((expression) => {
            app.dv.helpers.expressions.dispose(expression);
        });
        this.clientProperties.expressions = null;
        this.clientProperties.dispose();
        this.clientProperties = null;
        this.Definition.dispose();
        this.Definition = null;
        this.isDisposed = true;
    }
}

export const styleUtils = {
    colorHexRegex: new RegExp(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/),

    /**
     * Return an array of HEX colors based on specified string value
     * @param {string} value - String value representing HEX colors somewhere in the string
     * @return {Array} - Array of HEX color strings
     */
    parseHexColorsFromString: function (value) {
        if (value == null) {
            return [];
        }
        if (typeof value !== 'string') {
            return [];
        }
        return value.match(this.colorHexRegex);
    },

    /**
     * Clone the style to a new instance of an object.
     * @param {Style} style
     */
    cloneStyleInstance: function(style) {
        if (style == null) { return null; }

        //Create a clone of the style
        let clonedStyle = deepClone(style, "clientProperties");

        //Update the client properties with the style...eg
        app.dv.cache.styles.applyClientProperties(clonedStyle);
        clonedStyle.clientProperties.items = style.clientProperties.items.referenceClone();
        clonedStyle.clientProperties.expressions = style.clientProperties.expressions.referenceClone();
        clonedStyle.clientProperties.Tags = style.clientProperties.Tags.referenceClone();

        // these properties must be copied over as well to ensure consistent behavior.
        clonedStyle.clientProperties.isPinned = style.clientProperties.isPinned;

        //Return new instance of style...eg
        return clonedStyle;
    },

    /**
     * Create a new style that is the merge outcome of the specified sourceStyle and the mergeStyle.
     * NOTE: Client property Tags are not cloned or transferred.
     * NOTE: A "pinned" style being merged DOES NOT make the result style "pinned".
     * @param {Style|EntityLinker|null} [sourceStyle = null]
     * @param {Style|EntityLinker|null} [mergingStyle = null]
     * @param {boolean} [applyPinnedStyles = true] - TRUE, include pinned styles; FALSE, DO NOT include pinned styles.
     */
    mergeStyle: function (sourceStyle = null, mergingStyle = null, applyPinnedStyles = true) {
        if (sourceStyle == null && mergingStyle == null) {
            mergingStyle = new Style();
            mergingStyle.Name = mergingStyle.Definition.Name = mergingStyle.Definition.Description = "[merged style]";
            return mergingStyle;
        }

        /**
         * Flag for if the merging style is pinned.
         * @type {boolean}
         */
        let isMergePinned = false;

        /**
         * local variable for the new, "merged" style
         * @type {Style|null}
         */
        let newStyleObject = null;

        /*
         * There are 4 options:
         *  -   Source style is null and Merging style is null - return a "merged" (new) empty style object.
         *  -   Source style is given and Merging style is null - simply return a the source style.
         *  -   Source style is null and Merging style is given - simply return the merging style.
         *  -   Source style is given and Merging style is given - create a new "merged" style and merge in the source, and then the merging style.
         */

        // check if source style is a linker - if it is, attempt to get it.
        if (sourceStyle != null && typeof (sourceStyle) === 'object' && sourceStyle.Type === 'Style') {
            sourceStyle = app.dv.cache.styles.allStyles.getVal(sourceStyle);
        }

        // check if merging style is a linker - if it is, attempt to get it.
        if (mergingStyle != null) {
            if (typeof (mergingStyle) === 'object' && mergingStyle.Type === 'Style') {
                // check if the linker shows a pinned style.
                isMergePinned = (mergingStyle.Modifiers && mergingStyle.Modifiers.IsPinned) || false;

                if (isMergePinned && !applyPinnedStyles) {
                    // if the merge is pinned and we are not applying pinned styles, then we can't actually merge it...
                    mergingStyle = null;
                } else {
                    // otherwise, we can merge this style.
                    mergingStyle = app.dv.cache.styles.allStyles.getVal(mergingStyle);
                }
            }
            else if (mergingStyle instanceof Style) {
                // this is already a Style object - check if it is pinned.
                isMergePinned = mergingStyle.clientProperties.isPinned;

                if (isMergePinned && !applyPinnedStyles) {
                    // if the merge is pinned and we are not applying pinned styles, then we can't actually merge it...
                    mergingStyle = null;
                }
            }
        }

        // Now go through our options...
        if (sourceStyle != null && mergingStyle == null) {
            // Option 2 - source given, merging null; return the source style.
            newStyleObject = styleUtils.cloneStyleInstance(sourceStyle);
        }
        else if (sourceStyle == null && mergingStyle != null) {
            // Option 3 - source null, merging given; return merging style.
            newStyleObject = styleUtils.cloneStyleInstance(mergingStyle);
        }
        else if (sourceStyle != null && mergingStyle != null) {
            // Option 4 - source given, merging given; merge the styles.

            // get reference to the client properties of each source for ease of use.
            let cpSource = sourceStyle.clientProperties,
                cpMerge = mergingStyle.clientProperties;

            // create the merged style
            newStyleObject = new Style();
            newStyleObject.Name = newStyleObject.Definition.Name = newStyleObject.Definition.Description = "[merged style]";

            // merge the dictionaries in order onto the new style object. This will leave the source dictionaries unmodified.

            // create and merge the items.
            let newItems = newStyleObject.clientProperties.items = new Dictionary();

            // merge in the source style keys and values...
            newItems.merge(cpSource.items);

            // ...then merge in the merging style keys and values to overwrite any duplicates.
            newItems.merge(cpMerge.items);

            // NOTE: we do not merge expressions as we don't want them cached...

            // update the merged style definition.
            newStyleObject.Definition.ItemKeys = newItems.Keys.slice();
            newStyleObject.Definition.ItemValues = newItems.Values.slice();
        }
        else {
            // Option 1 - both are null; return a new style object.
            newStyleObject = new Style();
        }

        newStyleObject.Name = newStyleObject.Definition.Name = newStyleObject.Definition.Description = "[merged style]";
        return newStyleObject;
    },

    /**
     * Merge the two specified styles into a single combined style; the current overrides any parent values
     * @param {Style|EntityLinker|null} sourceStyle - The base style to start the merge with, or EntityLinker of the base style, or null.
     * @param {Style|EntityLinker|Array|null} mergeStyles - Style object or Style EntityLinker object or array of style objects.
     * @param {boolean} [applyPinnedStyles = true] - TRUE, include pinned styles; FALSE, DO NOT include pinned styles.
     * @return {Style} - The composite Style object.
     */
    mergeStyles: function (sourceStyle, mergeStyles, applyPinnedStyles = true) {
        let returnedStyle = styleUtils.mergeStyle(sourceStyle);
        if (Array.isArray(mergeStyles)) {
            mergeStyles.forEach((current) => {
                // the merge style function will consider pinned states for us, IF they are entity linkers...
                returnedStyle = styleUtils.mergeStyle(returnedStyle, current, applyPinnedStyles);
            });
            return returnedStyle;
        }
        return styleUtils.mergeStyle(returnedStyle, mergeStyles, applyPinnedStyles);
    },

    /**
     * Get the style associated with the current environment's current system theme.
     * @returns {Style|null}
     */
    getThemeDefaultStyle: function () {
        //If the uiTheme has a default style defined, get it and apply it at the base level...BC
        let uiTheme = app.dv.cache.session.configEntity.ConfigValue.SavedThemes.find(t => t.IsCurrent);

        if (uiTheme == null) {
            uiTheme = app.dv.cache.session.configEntity.ConfigValue.SavedThemes[ 0 ];
            uiTheme.IsCurrent = true;
        }

        //Need to re-pull the style because the object from the server doesn't have any methods...BC
        //uiTheme.DefaultStyle is sometimes a linker object, or null, or a style;  if it is null, it returns null which is
        //valid for the apply style.  If it is a linker or a style, it has the UID property which will be retrieved and used in the get val...BC
        return app.dv.cache.styles.allStyles.getVal(uiTheme.DefaultStyle);
    },

    /**
     * Get an inherited style or current style from the parent chain of the specified control. If no parent exists, then use the default theme style.
     * @param {zebra.ui.Layoutable} control - The control to start searching parents for inheritStyle or currentStyle.
     * @returns {Style}
     */
    getParentStyle: function (control) {
        //If we are a form with no styled parent, use the default theme instead
        let styleToApply = styleUtils.getThemeDefaultStyle(), p = control.parent;
        while (p != null) {
            if (p.inheritStyle != null) {
                // if there is a specific parent inherit style, use it as it would be what is passed down.
                styleToApply = p.inheritStyle;
                break;
            }
            else if (p.currentStyle != null) {
                // if there is no inherit style, but there is a current style, use it as it would be what is passed down.
                styleToApply = p.currentStyle;
                break;
            }
            p = p.parent;

        }

        return styleToApply;
    },

    /**
     * Search the specified layout item for the style entity specified by UID.
     * @param {LayoutItem} layoutItem - The definition layout item to search for the specified style UID
     * @param {String} styleUID - The UID of the style entity we are searching for.
     * @returns {Boolean} - TRUE, found the entity specified by UID; FALSE, did not find the entity specified by UID.
     */
    checkLayoutDefintionForStyle: function (layoutItem, styleUID) {
        if (Array.isArray(layoutItem.Styles)) {
            if (layoutItem.Styles.some(styleLinker => styleLinker.UID === styleUID)) {
                // found the entity in this definition object.
                return true;
            }
        }

        if (Array.isArray(layoutItem.Children)) {
            if (layoutItem.Children.some(child => styleUtils.checkLayoutDefintionForStyle(child, styleUID))) {
                // found the entity in one of the children layout objects.
                return true;
            }
        }

        // it was not found. :(
        return false;
    }
};

